<template>
  <CSidebar
    fixed
    :show="show"
    :minimize="minimize"
    @update:show="$emit('toggle-menu')"
  >
    <CSidebarBrand class="d-md-down-none">
      <router-link to="/">
        <img
          v-if="isMenuOpened && whiteLogo"
          :src="whiteLogo.url"
          :alt="whiteLogo.custom ? whiteLogo.custom.title : ''"
          :style="`max-height: ${minimize ? 20 : 30}px`"
        />
        <img
          v-else-if="!isMenuOpened && whiteLogoReduced"
          :src="whiteLogoReduced.url"
          :alt="whiteLogoReduced.custom ? whiteLogoReduced.custom.title : ''"
          :style="`max-height: ${minimize ? 20 : 30}px`"
        />
        <img
          v-else
          src="@/assets/img/logo-white.svg"
          alt="ow-logo-black"
          style="padding: 5px"
        />
      </router-link>
    </CSidebarBrand>
    <div
      class="sidebar-search"
      :style="`display: ${minimize ? 'none' : 'block'}`"
    >
      <div class="input-group">
        <input v-model="search" placeholder="Digite aqui para pesquisar..." />
      </div>
    </div>
    <CRenderFunction flat :content-to-render="filteredItems" />
    <CSidebarMinimizer @click.native="toggleMinimize"> </CSidebarMinimizer>
    <span
      v-if="version"
      class="sidebar-version"
      :style="`opacity: ${minimize ? '0' : '1'}; left: ${
        minimize ? '-100%' : '50%'
      }`"
    >
      <a href="/about">About OW</a>
    </span>
  </CSidebar>
</template>

<script>
import SettingService from '@/services/settings.service'
import { addListener } from 'process'
import { mapGetters } from 'vuex'
/**
 * Core UI Sidebar menu with search box and minimize option
 * @see https://coreui.io/vue/docs/components/template-components.html#csidebar-api
 */
export default {
  props: {
    /**
     * Array of links for build the sidemenu structure
     */
    items: {
      type: Array,
      default: () => []
    },

    /**
     * Show sidemenu
     */
    show: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      search: '',
      isCurrentPage: false,
      minimize: false,
      version: '',
      whiteLogo: {},
      whiteLogoReduced: {},
      isMenuOpened: true,
      allowedLinks: [
        {
          _name: 'CSidebarNav',
          _children: []
        }
      ]
    }
  },

  computed: {
    ...mapGetters('auth', ['permissions']),
    /**
     * Search input by name in parent objects first
     * if no results, searchs in child objects if any
     * if search data is empty no search is made
     */
    filteredItems() {
      let filtered = [
        {
          _name: 'CSidebarNav',
          _children: []
        }
      ]

      if (this.search !== '') {
        const resultsByParent = this.allowedLinks[0]._children.filter(
          (item) => {
            if (item.name) {
              return item.name.toLowerCase().includes(this.search.toLowerCase())
            }
          }
        )

        if (resultsByParent.length > 0) {
          filtered[0]._children.push(resultsByParent[0])
        } else {
          const resultsByChildren = this.allowedLinks[0]._children.filter(
            (child) => {
              if (child.items) {
                return child.items.filter((item) => {
                  return item.name
                    .toLowerCase()
                    .includes(this.search.toLowerCase())
                })
              }
            }
          )
          if (resultsByChildren.length > 0) {
            filtered[0]._children.push(resultsByChildren[0])
          }
        }
      } else {
        filtered = this.allowedLinks
      }

      return filtered
    }
  },

  watch: {
    permissions: {
      handler() {
        this.getAllowedMenuLinks()
      },
      deep: true
    }
  },

  mounted() {
    this.getVersion()
    this.getLogo()
    this.handlerWindowSize()
    this.getAllowedMenuLinks()
  },

  methods: {
    getAllowedMenuLinks() {
      this.allowedLinks = [
        {
          _name: 'CSidebarNav',
          _children: []
        }
      ]

      this.items[0]._children.forEach((item) => {
        if (item.to === '/dashboard') {
          if (this.permissions.includes('manage_dashboard')) {
            this.allowedLinks[0]._children.push(item)
          }
        } else if (item.to === '/banners') {
          if (this.permissions.includes('manage_banners')) {
            this.allowedLinks[0]._children.push(item)
          }
        } else if (item.to === '/popups') {
          if (this.permissions.includes('manage_popups')) {
            this.allowedLinks[0]._children.push(item)
          }
        } else if (item.to === '/work-with-us') {
          if (this.permissions.includes('manage_work_with_us')) {
            this.allowedLinks[0]._children.push(item)
          }
        } else if (item.to === '/newsletter') {
          if (this.permissions.includes('manage_newsletters')) {
            this.allowedLinks[0]._children.push(item)
          }
        } else if (item.to === '/cookies') {
          if (this.permissions.includes('manage_cookies')) {
            this.allowedLinks[0]._children.push(item)
          }
        } else if (item.to === '/seo') {
          if (this.permissions.includes('manage_seo')) {
            this.allowedLinks[0]._children.push(item)
          }
        } else if (item.to === '/parameters') {
          if (this.permissions.includes('manage_work_with_us')) {
            this.allowedLinks[0]._children.push(item)
          }
        } else {
          this.allowedLinks[0]._children.push(item)
        }
      })

      this.allowedLinks[0]._children.map((item) => {
        let allowedSubItems = []
        if (item.items) {
          allowedSubItems = item.items.filter((subItem) => {
            if (subItem.to === '/institutional') {
              return this.permissions.includes('manage_institutionals')
            } else if (subItem.to === '/timeline') {
              return this.permissions.includes('manage_timelines')
            } else if (subItem.to === '/direction') {
              return this.permissions.includes('manage_directorships')
            } else if (subItem.to === '/direction') {
              return this.permissions.includes('manage_directorships')
            } else if (subItem.to === '/certificates') {
              return this.permissions.includes('manage_certificates')
            } else if (subItem.to === '/products') {
              return this.permissions.includes('manage_products')
            } else if (subItem.to === '/subproducts') {
              return this.permissions.includes('manage_products')
            } else if (subItem.to === '/products-category') {
              return this.permissions.includes('manage_products')
            } else if (subItem.to === '/products-subcategory') {
              return this.permissions.includes('manage_products')
            } else if (subItem.to === '/solutions') {
              return this.permissions.includes('manage_solutions')
            } else if (subItem.to === '/aplications') {
              return this.permissions.includes('manage_applications')
            } else if (subItem.to === '/news') {
              return this.permissions.includes('manage_news')
            } else if (subItem.to === '/news-category') {
              return this.permissions.includes('manage_news')
            } else if (subItem.to === '/publications') {
              return this.permissions.includes('manage_publications')
            } else if (subItem.to === '/chapters') {
              return this.permissions.includes('manage_sections')
            } else if (subItem.to === '/departament') {
              return this.permissions.includes('manage_departments')
            } else if (subItem.to === '/contact') {
              return this.permissions.includes('manage_contacts')
            } else if (subItem.to === '/newsletter') {
              return this.permissions.includes('manage_newsletters')
            } else if (subItem.to === '/users-restrict') {
              return this.permissions.includes('manage_restricted_area_users')
            } else if (subItem.to === '/sections') {
              return this.permissions.includes(
                'manage_restricted_area_sections'
              )
            } else if (subItem.to === '/settings') {
              return this.permissions.includes('manage_settings')
            } else if (subItem.to === '/users') {
              return this.permissions.includes('manage_users')
            } else if (subItem.to === '/seo') {
              return this.permissions.includes('manage_seo')
            } else if (subItem.to === '/cookies') {
              return this.permissions.includes('manage_cookies')
            } else if (subItem.to === '/parameters/education') {
              return this.permissions.includes('manage_work_with_us')
            } else if (subItem.to === '/parameters/interest') {
              return this.permissions.includes('manage_work_with_us')
            } else if (subItem.to === '/parameters/training') {
              return this.permissions.includes('manage_work_with_us')
            }
          })
        }

        item.items = allowedSubItems
      })

      this.allowedLinks[0]._children = this.allowedLinks[0]._children.filter(
        (item) => {
          return item.items.length > 0 || item._name === 'CSidebarNavItem'
        }
      )
    },
    /**
     * Get VUE app version
     */
    getVersion() {
      this.version = process.env.VUE_APP_VERSION
    },

    /**
     * Get Logo
     */
    async getLogo() {
      const data = await SettingService.listAllMedias()
      this.whiteLogo = data.whiteLogo && data.whiteLogo[0]
      this.whiteLogoReduced = data.whiteLogoReduced && data.whiteLogoReduced[0]
    },

    /**
     * Switch between minimize state of Sidebar.
     * @see https://coreui.io/vue/docs/components/template-components.html#csidebar-api
     * @public
     */
    toggleMinimize: function() {
      this.minimize = !this.minimize
      document.body.classList.toggle('sidebarIsOpen')
      this.isMenuOpened = !this.isMenuOpened
    },
    /**
     * Verify and sets size of Pagination component
     */
    handlerWindowSize() {
      const handler = () => {
        if (window.innerWidth <= 992) {
          // add class to be used as flag on loading
          document.body.classList.remove('sidebarIsOpen')
        } else {
          document.body.classList.add('sidebarIsOpen')
        }
      }

      window.addEventListener('resize', () => {
        handler()
      })

      handler()
    }
  }
}
</script>

<style lang="scss" scoped>
.currentPageBorder {
  border-left: 2px solid $danger-dark;
}

.sidebar-version {
  position: absolute;
  bottom: 0px;
  height: 50px;
  line-height: 50px;
  // z-index: -1;
  color: $white;
  transform: translateX(-50%);
  transition: 300ms ease;

  a {
    text-decoration: none;
    color: inherit;
  }
}

.sidebar-search {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  padding: 0.75rem 1rem;
  transition: 300ms ease;

  .input-group {
    input {
      border: 0px;
      border-bottom: 1px solid $white;
      min-height: 40px;
      height: 40px;
      font-size: 14px;
      padding: 10px 5px;
      width: 100%;
      background-color: $gray-base;
      outline: none;
      color: $white;

      &::placeholder {
        color: $white;
        opacity: 1; /* Firefox */
      }

      &:-ms-input-placeholder {
        color: $white;
      }

      &::-ms-input-placeholder {
        color: $white;
      }
    }

    .btn-outline-secondary {
      border-color: rgba(94, 101, 117, 0.32);
    }
  }
}

@media only screen and (max-width: 992px) {
  .sidebar-version {
    display: none;
  }
}
</style>
